// src/pages/public/Packages.js
import React, { useState } from "react";
import { Helmet } from 'react-helmet-async';
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { PACKAGES, MAYA_FEES } from "../../utils/constants";
import { useAuth } from "../../context/AuthContext";

const COLOR_MAP = {
  blue:   { bg: "rgba(59,130,246,0.08)",  border: "rgba(59,130,246,0.25)",  text: "#60a5fa" },
  teal:   { bg: "rgba(0,201,167,0.08)",   border: "rgba(0,201,167,0.3)",    text: "var(--accent)" },
  purple: { bg: "rgba(168,85,247,0.08)",  border: "rgba(168,85,247,0.25)",  text: "#c084fc" },
  amber:  { bg: "rgba(245,158,11,0.08)",  border: "rgba(245,158,11,0.25)",  text: "var(--accent-warm)" },
  coral:  { bg: "rgba(239,68,68,0.08)",   border: "rgba(239,68,68,0.25)",   text: "#f87171" },
  gray:   { bg: "rgba(100,116,139,0.08)", border: "rgba(100,116,139,0.25)", text: "#94a3b8" },
};

function formatPrice(min, max, billing) {
  const fmt = n => "₱" + n.toLocaleString();
  if (!max) return fmt(min) + "+";
  return fmt(min) + " – " + fmt(max);
}

export default function Packages() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);

  // Maya fee calculator state
  const [calcAmount, setCalcAmount] = useState(10000);
  const [calcMethod, setCalcMethod] = useState("qrCode");

  const fee = MAYA_FEES[calcMethod];
  const deduction = Math.round(calcAmount * fee.total);
  const received = calcAmount - deduction;

  const handleCheckout = (pkg) => {
    if (!currentUser) {
      navigate("/login?redirect=/checkout&pkg=" + pkg.id);
    } else {
      navigate("/checkout?pkg=" + pkg.id);
    }
  };

  return (
    <div style={{ paddingTop: "var(--header-h)" }}>
      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <Helmet>
        <title>Packages - Aimble I.T. Solutions</title>
        <meta name="description" content="Explore web development and IT service packages from Aimble I.T. Solutions. Get transparent pricing for custom websites, payment integration, and VA services." />
        <link rel="canonical" href="https://www.aimbletech.com/packages" />
      </Helmet>
      <section className="section-sm" style={{ textAlign: "center" }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>
            <div className="section-label" style={{ justifyContent: "center" }}>Pricing</div>
            <h1 className="heading-1" style={{ marginBottom: 16 }}>Simple, transparent pricing</h1>
            <p className="body-lg text-muted" style={{ maxWidth: 520, margin: "0 auto 32px" }}>
              Choose a package or get a custom quote. All prices are in Philippine Peso. Official BIR receipts issued on payment.
            </p>
            <div style={{ display: "inline-flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
              <span className="badge badge-accent">✓ Official Receipts</span>
              <span className="badge badge-blue">✓ Maya / GCash / CC Payments</span>
              <span className="badge badge-amber">✓ BIR & DTI Registered</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── PACKAGES GRID ──────────────────────────────────────────────────── */}
      <section className="section-sm">
        <div className="container">
          {/* One-time packages */}
          <h2 style={{ fontSize: "1rem", fontWeight: 600, color: "var(--text-muted)", marginBottom: 24, letterSpacing: "0.08em", textTransform: "uppercase" }}>One-Time Projects</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 24, marginBottom: 48 }}>
            {PACKAGES.filter(p => p.billingType === "one-time").map((pkg, i) => {
              const col = COLOR_MAP[pkg.color] || COLOR_MAP.blue;
              const isFeatured = pkg.badge === "Most Popular";
              return (
                <motion.div key={pkg.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                  <div
                    className="card"
                    style={{
                      border: isFeatured ? `2px solid ${col.border}` : "1px solid var(--border)",
                      position: "relative",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "border-color 0.2s, box-shadow 0.2s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = col.border; e.currentTarget.style.boxShadow = `0 0 32px ${col.bg}`; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = isFeatured ? col.border : "var(--border)"; e.currentTarget.style.boxShadow = "none"; }}
                  >
                    {pkg.badge && (
                      <div style={{ position: "absolute", top: -1, right: 20 }}>
                        <span style={{ background: col.bg, color: col.text, border: `1px solid ${col.border}`, borderRadius: "0 0 8px 8px", padding: "4px 12px", fontSize: "0.6875rem", fontWeight: 700, letterSpacing: "0.06em" }}>{pkg.badge.toUpperCase()}</span>
                      </div>
                    )}
                    <div style={{ marginBottom: 24 }}>
                      <h3 style={{ fontSize: "1.125rem", fontWeight: 700, marginBottom: 4, fontFamily: "var(--font-head)", color: col.text }}>{pkg.name}</h3>
                      <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: 16 }}>{pkg.description}</p>
                      <div style={{ fontSize: "1.5rem", fontWeight: 800, fontFamily: "var(--font-head)", color: "var(--text)" }}>
                        {formatPrice(pkg.price, pkg.priceMax, pkg.billingType)}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: 2 }}>one-time payment</div>
                    </div>

                    <ul style={{ listStyle: "none", flex: 1, marginBottom: 24 }}>
                      {pkg.features.map(f => (
                        <li key={f} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 10, fontSize: "0.875rem", color: "var(--text-muted)" }}>
                          <span style={{ color: col.text, flexShrink: 0, marginTop: 1 }}>✓</span>
                          {f}
                        </li>
                      ))}
                    </ul>

                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        className="btn btn-primary"
                        style={{ flex: 1, background: col.text, color: "var(--navy)", justifyContent: "center" }}
                        onClick={() => handleCheckout(pkg)}
                      >
                        {pkg.cta}
                      </button>
                      <Link to="/contact" className="btn btn-secondary btn-sm" style={{ whiteSpace: "nowrap" }}>Get Quote</Link>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Monthly packages */}
          <h2 style={{ fontSize: "1rem", fontWeight: 600, color: "var(--text-muted)", marginBottom: 24, letterSpacing: "0.08em", textTransform: "uppercase" }}>Monthly Plans</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 24, marginBottom: 64 }}>
            {PACKAGES.filter(p => p.billingType === "monthly").map((pkg, i) => {
              const col = COLOR_MAP[pkg.color] || COLOR_MAP.blue;
              return (
                <motion.div key={pkg.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                  <div className="card" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    <h3 style={{ fontSize: "1.125rem", fontWeight: 700, marginBottom: 4, fontFamily: "var(--font-head)", color: col.text }}>{pkg.name}</h3>
                    <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: 16 }}>{pkg.description}</p>
                    <div style={{ fontSize: "1.5rem", fontWeight: 800, fontFamily: "var(--font-head)", color: "var(--text)" }}>
                      {formatPrice(pkg.price, pkg.priceMax, pkg.billingType)}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: 2, marginBottom: 20 }}>per month</div>
                    <ul style={{ listStyle: "none", flex: 1, marginBottom: 24 }}>
                      {pkg.features.map(f => (
                        <li key={f} style={{ display: "flex", gap: 8, marginBottom: 10, fontSize: "0.875rem", color: "var(--text-muted)" }}>
                          <span style={{ color: col.text, flexShrink: 0 }}>✓</span>{f}
                        </li>
                      ))}
                    </ul>
                    <button className="btn btn-secondary w-full" style={{ justifyContent: "center" }} onClick={() => handleCheckout(pkg)}>
                      {pkg.cta}
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* ── MAYA PAYMENT FEE CALCULATOR ─────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div style={{ background: "var(--surface)", border: "1px solid var(--border-med)", borderRadius: 20, padding: "40px 32px" }}>
              <div style={{ marginBottom: 24 }}>
                <div className="section-label">Payment Gateway</div>
                <h2 className="heading-2">Maya Payment Fee Calculator</h2>
                <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", marginTop: 8 }}>
                  If you want your customers to pay via our Maya gateway, use this calculator to understand the net amount you'll receive after all deductions.
                </p>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
                <div>
                  <div className="form-group">
                    <label className="form-label">Transaction Amount (₱)</label>
                    <input
                      type="number"
                      className="form-input"
                      value={calcAmount}
                      min={100}
                      onChange={e => setCalcAmount(Number(e.target.value))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Payment Method</label>
                    <select className="form-select" value={calcMethod} onChange={e => setCalcMethod(e.target.value)}>
                      {Object.entries(MAYA_FEES).map(([key, val]) => (
                        <option key={key} value={key}>{val.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ fontSize: "0.8125rem", color: "var(--text-dim)", lineHeight: 1.6 }}>
                    {fee.description}
                  </div>
                </div>

                <div>
                  <div style={{ background: "var(--navy-mid)", borderRadius: 12, padding: 24 }}>
                    <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: "1px solid var(--border)" }}>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: 4, letterSpacing: "0.05em" }}>TRANSACTION AMOUNT</div>
                      <div style={{ fontSize: "1.5rem", fontWeight: 700, fontFamily: "var(--font-head)" }}>₱{calcAmount.toLocaleString()}</div>
                    </div>

                    {fee.total > 0 ? (
                      <>
                        {fee.birTax > 0 && <FeeRow label="BIR Sales Tax (3%)" amount={Math.round(calcAmount * fee.birTax)} color="#f87171" />}
                        {fee.mayaFee > 0 && <FeeRow label={`Maya Processing (${(fee.mayaFee * 100).toFixed(1)}%)`} amount={Math.round(calcAmount * fee.mayaFee)} color="#fbbf24" />}
                        {fee.buffer > 0 && <FeeRow label="Service Buffer (0.5%)" amount={Math.round(calcAmount * fee.buffer)} color="#a78bfa" />}
                        <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16, marginTop: 16 }}>
                          <FeeRow label={`Total Deductions (${(fee.total * 100).toFixed(1)}%)`} amount={deduction} color="#f87171" bold />
                        </div>
                      </>
                    ) : (
                      <div style={{ color: "var(--accent)", fontSize: "0.875rem", marginBottom: 16 }}>✓ No deductions — client uses their own QR</div>
                    )}

                    <div style={{ background: "rgba(0,201,167,0.08)", border: "1px solid rgba(0,201,167,0.2)", borderRadius: 10, padding: "16px 20px", marginTop: 16 }}>
                      <div style={{ fontSize: "0.75rem", color: "var(--accent)", marginBottom: 4, fontWeight: 600 }}>YOU RECEIVE</div>
                      <div style={{ fontSize: "2rem", fontWeight: 800, fontFamily: "var(--font-head)", color: "var(--accent)" }}>₱{received.toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* ── CUSTOM QUOTE CTA ─────────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ marginTop: 48, textAlign: "center" }}>
            <p className="body-lg text-muted" style={{ marginBottom: 20 }}>Don't see the right fit? We'll build a custom quote for you.</p>
            <Link to="/contact" className="btn btn-primary btn-lg">Request Custom Quote</Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function FeeRow({ label, amount, color, bold }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, fontSize: bold ? "0.9375rem" : "0.875rem" }}>
      <span style={{ color: "var(--text-muted)", fontWeight: bold ? 600 : 400 }}>{label}</span>
      <span style={{ color: color || "var(--text)", fontWeight: bold ? 700 : 500 }}>- ₱{amount.toLocaleString()}</span>
    </div>
  );
}
